import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useClients, useDeleteClient } from '@/hooks/use-clients';
import { useOpenCreateFromQuery } from '@/hooks/use-open-create-from-query';
import { useSalesDocuments } from '@/hooks/use-sales';
import { usePayments } from '@/hooks/use-payments';
import { computeClientBalance } from '@erp/domain/payments';
import type { Client, ClientFilters, SalesDocument, Payment } from '@erp/data-access/interfaces';
import { ClientFormDialog } from './ClientFormDialog';
import {
  Badge, Button, Card, CardContent, CardHeader,
  DataTable, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  EmptyState, FilterBar, FilterField, Input, PageHeader,
  cn,
  type ColumnDef,
} from '@erp/ui';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Users } from '@/components/icons';
import { useColumnConfig, ColumnConfigDialog, ColumnConfigButton, type ColDef } from '@/components/ui/column-config';
import { SearchableSelect } from '@/components/ui/searchable-select';

const ALL_COLUMNS: ColDef[] = [
  { key: 'code', labelKey: 'clients.code', locked: true },
  { key: 'name', labelKey: 'clients.name' },
  { key: 'type', labelKey: 'clients.type' },
  { key: 'taxId', labelKey: 'clients.taxId' },
  { key: 'city', labelKey: 'clients.city' },
  { key: 'phone', labelKey: 'clients.phone' },
  { key: 'email', labelKey: 'clients.email' },
  { key: 'defaultDiscount', labelKey: 'clients.defaultDiscount' },
  { key: 'paymentTerms', labelKey: 'clients.paymentTerms' },
  { key: 'balance', labelKey: 'payments.balance' },
];

const DEFAULT_COLUMNS = ['code', 'name', 'type', 'taxId', 'city', 'phone', 'balance'];

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function ClientsPage() {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editClient, setEditClient] = useState<Client | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Client | null>(null);
  const [colConfigOpen, setColConfigOpen] = useState(false);

  const { visibleCols, toggleCol, moveCol, resetCols, allColumns } = useColumnConfig({
    storageKey: 'erp_client_columns',
    allColumns: ALL_COLUMNS,
    defaultColumns: DEFAULT_COLUMNS,
  });

  const filters: ClientFilters = useMemo(() => ({
    search: search || undefined,
    type: (typeFilter as 'company' | 'individual') || undefined,
  }), [search, typeFilter]);

  const { data: clients, isLoading } = useClients(filters);
  const { data: allSalesDocs } = useSalesDocuments();
  const { data: allPayments } = usePayments({ thirdPartyType: 'client' });
  const deleteMutation = useDeleteClient();

  const balances = useMemo(() => {
    const map = new Map<string, number>();
    if (!clients) return map;
    for (const c of clients) {
      const clientPayments = (allPayments ?? []).filter((p: Payment) => p.thirdPartyId === c.id);
      const clientDocs = (allSalesDocs ?? [])
        .filter((d: SalesDocument) => d.clientId === c.id && (d.docType === 'invoice' || d.docType === 'credit_note'))
        .map((d: SalesDocument) => ({
          docType: d.docType as 'invoice' | 'credit_note',
          netToPay: Number(d.netToPay) > 0 ? Number(d.netToPay) : Number(d.totalTtc),
        }));
      map.set(c.id, computeClientBalance(
        clientPayments.map((p) => ({ amount: p.amount, paymentType: p.paymentType })),
        clientDocs,
      ));
    }
    return map;
  }, [clients, allSalesDocs, allPayments]);

  const handleEdit = (c: Client) => { setEditClient(c); setFormOpen(true); };
  const handleNew = () => { setEditClient(null); setFormOpen(true); };
  useOpenCreateFromQuery(handleNew);
  const handleDelete = async () => {
    if (toDelete) { await deleteMutation.mutateAsync(toDelete.id); setDeleteOpen(false); }
  };

  const allColumnDefs: Record<string, ColumnDef<Client, unknown>> = {
    code: {
      accessorKey: 'code',
      header: t('clients.code'),
      cell: ({ row }) => <span className="font-mono text-xs uppercase tracking-wide text-muted-foreground">{row.original.code}</span>,
    },
    name: {
      accessorKey: 'name',
      header: t('clients.name'),
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    type: {
      accessorKey: 'type',
      header: t('clients.type'),
      enableSorting: false,
      cell: ({ row }) => (
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] font-medium',
            row.original.type === 'company'
              ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400'
              : 'border-border bg-muted/50 text-muted-foreground',
          )}
        >
          {row.original.type === 'company' ? t('clients.typeCompany') : t('clients.typeIndividual')}
        </Badge>
      ),
    },
    taxId: { accessorKey: 'taxId', header: t('clients.taxId'), cell: ({ row }) => row.original.taxId ?? <span className="text-muted-foreground">—</span> },
    city: { accessorKey: 'city', header: t('clients.city'), cell: ({ row }) => row.original.city ?? <span className="text-muted-foreground">—</span> },
    phone: { accessorKey: 'phone', header: t('clients.phone'), cell: ({ row }) => row.original.phone ?? <span className="text-muted-foreground">—</span> },
    email: { accessorKey: 'email', header: t('clients.email'), cell: ({ row }) => row.original.email ?? <span className="text-muted-foreground">—</span> },
    defaultDiscount: {
      id: 'defaultDiscount',
      header: t('clients.defaultDiscount'),
      enableSorting: false,
      cell: ({ row }) => row.original.defaultDiscountPct != null ? `${row.original.defaultDiscountPct}%` : <span className="text-muted-foreground">—</span>,
    },
    paymentTerms: {
      id: 'paymentTerms',
      header: t('clients.paymentTerms'),
      enableSorting: false,
      cell: ({ row }) => row.original.paymentTermsDays != null ? `${row.original.paymentTermsDays} j` : <span className="text-muted-foreground">—</span>,
    },
    balance: {
      id: 'balance',
      header: () => <span className="block text-end">{t('payments.balance')}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const b = balances.get(row.original.id) ?? 0;
        if (Math.abs(b) < 0.001) return <span className="block text-end text-muted-foreground tabular-nums">0,000 DT</span>;
        const isCredit = b > 0;
        return (
          <span
            className={cn(
              'block text-end tabular-nums font-medium',
              isCredit ? 'text-green-600 dark:text-green-400' : 'text-destructive',
            )}
            title={isCredit ? t('payments.creditBalance') : t('payments.debtBalance')}
          >
            {isCredit ? '+' : ''}{formatPrice(b)} DT
          </span>
        );
      },
    },
  };

  const columns: ColumnDef<Client, unknown>[] = [
    ...visibleCols.map((k) => allColumnDefs[k]).filter(Boolean) as ColumnDef<Client, unknown>[],
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-xs">
                <MoreHorizontal size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                <Pencil size={14} />{t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={() => { setToDelete(row.original); setDeleteOpen(true); }}>
                <Trash2 size={14} />{t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const hasActiveFilters = Boolean(search || typeFilter);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('clients.title')}
        actions={
          <Button onClick={handleNew}>
            <Plus data-icon="inline-start" size={16} />
            {t('clients.newClient')}
          </Button>
        }
      />

      <Card className="transition-all duration-200">
        <CardHeader className="pb-3">
          <FilterBar
            onClear={hasActiveFilters ? () => { setSearch(''); setTypeFilter(''); } : undefined}
            clearLabel={t('common.clearFilters')}
            rightSlot={<ColumnConfigButton onClick={() => setColConfigOpen(true)} />}
          >
            <FilterField label={t('common.search')} grow>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none start-2.5"
                />
                <Input
                  placeholder={t('common.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-8 h-8"
                />
              </div>
            </FilterField>
            <FilterField label={t('clients.type')} minWidth={200}>
              <SearchableSelect
                options={[{ value: 'company', label: t('clients.typeCompany') }, { value: 'individual', label: t('clients.typeIndividual') }]}
                value={typeFilter}
                onChange={setTypeFilter}
                label={t('clients.type')}
                placeholder={t('common.all')}
                searchPlaceholder={t('common.search')}
                className="h-8"
              />
            </FilterField>
          </FilterBar>
        </CardHeader>
        <CardContent className="pt-0">
          <DataTable
            data={clients ?? []}
            columns={columns}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`/clients/${r.id}`)}
            isLoading={isLoading}
            emptyState={
              <EmptyState
                icon={<Users size={18} />}
                title={t('clients.noResults', { defaultValue: t('common.noResults') })}
                description={t('clients.emptyHint', { defaultValue: '' })}
                action={<Button variant="outline" size="sm" onClick={handleNew}>
                    <Plus data-icon="inline-start" size={14} />
                    {t('clients.newClient')}
                  </Button>
                }
              />
            }
            pagination
            pageSize={10}
            rowsPerPageLabel={t('dashboard.rowsPerPage')}
            ofLabel={t('dashboard.of')}
            minWidth={720}
          />
        </CardContent>
      </Card>

      <ClientFormDialog open={formOpen} onOpenChange={setFormOpen} client={editClient} />

      <ColumnConfigDialog
        open={colConfigOpen}
        onOpenChange={setColConfigOpen}
        visibleCols={visibleCols}
        allColumns={allColumns}
        toggleCol={toggleCol}
        moveCol={moveCol}
        resetCols={resetCols}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('clients.deleteConfirm')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('clients.deleteWarning')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
